import { defineStore } from 'pinia'
import { ref } from 'vue'
import { parseJSON } from '@/utils'

interface MqttConfig {
  device_identifier: string
  access_token: string
  session_id: string
}

// Define message types for WebSocket communication
interface WsMessage {
  type?: string
  topic?: string
  payload?: unknown
  error?: string

  [key: string]: unknown
}

export const useMqttStore = defineStore('mqtt', () => {
  const messages = ref<Record<string, unknown>>({})
  const isConnected = ref<boolean>(false)
  const socket = ref<WebSocket | null>(null)
  const status = ref<string>('')
  const error = ref<string>('')
  const subscribers = ref(new Map<string, Set<(value: unknown) => void>>())
  const deviceId = ref<string>('')
  const sessionId = ref<string>('')

  // Enhanced connection status tracking
  const connectionStatus = ref<string>('Initializing...')
  const hasAuthData = ref<boolean>(false)
  const isConnecting = ref<boolean>(false)
  const lastError = ref<string>('')

  // Demo device error handling
  const demoDeviceError = ref<{
    error: string
    message: string
    device_id: string
    command: string
    datapoint?: string
    attempted_value?: string | number | boolean
    ackRequired?: boolean
  } | null>(null)
  const showDemoDeviceError = ref<boolean>(false)

  const connect = async (config: MqttConfig): Promise<void> => {
    if (import.meta.env.DEV) {
      console.log('Connecting to MQTT bridge with config:', config)
    }

    // Store device ID for topic validation
    deviceId.value = config.device_identifier
    sessionId.value = config.session_id
    hasAuthData.value = true
    isConnecting.value = true
    connectionStatus.value = 'Connecting to MQTT bridge...'
    lastError.value = ''

    if (socket.value && socket.value.readyState === WebSocket.OPEN) {
      if (import.meta.env.DEV) {
        console.log('Active WebSocket connection already exists')
      }
      connectionStatus.value = 'Already connected'
      isConnecting.value = false
      return
    }

    if (socket.value) {
      if (import.meta.env.DEV) {
        console.log('Cleaning up existing WebSocket connection')
      }
      socket.value.close()
    }

    if (import.meta.env.DEV) {
      console.log('Creating new WebSocket connection to MQTT bridge')
    }
    connectionStatus.value = 'Establishing WebSocket connection...'
    // Connect to the WebSocket server
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws'
    const wsClient = new WebSocket(wsUrl)
    socket.value = wsClient

    wsClient.onopen = () => {
      if (import.meta.env.DEV) {
        console.log('WebSocket connection established, sending authentication')
      }
      connectionStatus.value = 'Authenticating with Bridge...'

      // Send authentication message with device_identifier and access_token
      const authMessage = {
        device_identifier: config.device_identifier,
        access_token: config.access_token,
        session_id: config.session_id,
      }

      wsClient.send(JSON.stringify(authMessage))
    }

    wsClient.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as WsMessage

        // Check for error messages
        if (message.error) {
          // Check if this is a demo device error
          if (message.error === 'THIS IS A DEMO device!!!') {
            if (import.meta.env.DEV) {
              console.log('Demo device error detected:', message)
            }
            demoDeviceError.value = {
              error: message.error as string,
              message: message.message as string,
              device_id: message.device_id as string,
              command: message.command as string,
              datapoint: message.datapoint as string,
              attempted_value: message.attempted_value as string | number | boolean,
              ackRequired: message.ackRequired as boolean,
            }
            showDemoDeviceError.value = true
            return // Don't process this as a regular bridge error
          }

          // Handle other bridge errors
          console.error('Bridge error:', message.error)
          error.value = `Bridge error: ${message.error}`
          lastError.value = message.error
          connectionStatus.value = `Error: ${message.error}`
          isConnecting.value = false
          return
        }

        // If we get here and we're not connected yet, it means auth was successful
        if (!isConnected.value) {
          if (import.meta.env.DEV) {
            console.log('Authentication successful')
          }
          isConnected.value = true
          status.value = 'Connected'
          connectionStatus.value = 'Connected to MQTT bridge'
          isConnecting.value = false
        }

        // Process incoming MQTT message
        if (message.topic && message.payload !== undefined) {
          const topic = message.topic as string
          let parsedPayload: unknown = message.payload

          // If payload is a string that looks like JSON, try to parse it
          if (typeof message.payload === 'string') {
            try {
              parsedPayload = parseJSON(message.payload as string)
              // Additional check to ensure we got a valid object/array
              if (parsedPayload === null || typeof parsedPayload !== 'object') {
                parsedPayload = message.payload
              }
            } catch {
              parsedPayload = message.payload
            }
          }

          if (import.meta.env.DEV) {
            console.log(`Received message on topic ${topic}:`, parsedPayload)
          }
          const path = topic.split('/')
          setNestedValue(messages.value, path, parsedPayload)
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error)
      }
    }

    wsClient.onclose = (event) => {
      if (import.meta.env.DEV) {
        console.log(`WebSocket connection closed: ${event.code} ${event.reason}`)
      }
      isConnected.value = false
      status.value = 'Disconnected'
      connectionStatus.value = `Connection closed: ${event.reason || 'Unknown reason'}`
      isConnecting.value = false
      // Attempt to reconnect after 1 second with same config
      setTimeout(() => connect(config), 1000)
    }

    wsClient.onerror = (err) => {
      console.error('WebSocket error:', err)
      isConnected.value = false
      error.value = 'WebSocket connection error'
      lastError.value = 'WebSocket connection error'
      connectionStatus.value = 'WebSocket connection error'
      isConnecting.value = false
    }
  }

  const disconnect = (): void => {
    if (socket.value) {
      if (import.meta.env.DEV) {
        console.log('Disconnecting WebSocket client')
      }
      socket.value.close()
      socket.value = null
      isConnected.value = false
      status.value = 'Disconnected'
    }
  }

  const getValue = (path: string): unknown => {
    const keys = path.split('/')
    let current: unknown = messages.value

    for (const key of keys) {
      if (
        current === undefined ||
        current === null ||
        typeof current !== 'object' ||
        !(key in (current as Record<string, unknown>))
      ) {
        return undefined
      }
      current = (current as Record<string, unknown>)[key]
    }
    return current
  }

  const setNestedValue = (obj: Record<string, unknown>, path: string[], value: unknown): void => {
    let current = obj

    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i]
      if (key && !(key in current)) {
        current[key] = {}
      }
      if (key) {
        current = current[key] as Record<string, unknown>
      }
    }

    const lastKey = path[path.length - 1]
    if (lastKey) {
      current[lastKey] = value
    }

    const topicPath = path.join('/')
    const callbacks = subscribers.value.get(topicPath)
    if (callbacks) {
      callbacks.forEach((callback) => callback(value))
    }
  }

  const subscribe = (path: string, callback: (value: unknown) => void): (() => void) => {
    // Create wrapper function that handles special cases
    const wrappedCallback = (value: unknown) => {
      // Special case for screen data (base64) and other raw data topics
      if (path.endsWith('/screen')) {
        callback(value)
      } else if (value && (typeof value === 'string' || typeof value === 'object')) {
        callback(parseJSON(value) || {})
      } else {
        callback(value)
      }
    }

    const currentValue = getValue(path)
    if (currentValue !== undefined) {
      wrappedCallback(currentValue)
    }

    if (!subscribers.value.has(path)) {
      subscribers.value.set(path, new Set())
    }

    subscribers.value.get(path)?.add(wrappedCallback)

    return () => {
      subscribers.value.get(path)?.delete(wrappedCallback)
      if (subscribers.value.get(path)?.size === 0) {
        subscribers.value.delete(path)
      }
    }
  }

  /**
   * `qos` is optional and only reaches the broker on a bridge that understands
   * it; older bridges ignore the extra field and publish at their default QoS 0.
   * QoS 2 is deliberately not offered — its four-way handshake doubles latency
   * on exactly the connections this matters for, and buys nothing, because no
   * command the app sends is harmed by arriving twice.
   */
  const publish = (topic: string, payload: unknown, qos?: 0 | 1): void => {
    if (!socket.value || socket.value.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected')
      return
    }

    // Ensure topic starts with device ID
    if (!topic.startsWith(`${deviceId.value}/`)) {
      console.warn(`Topic ${topic} does not start with device ID ${deviceId.value}, adding prefix`)
      topic = `${deviceId.value}/${topic}`
    }

    // Format message according to the Python bridge's expected format
    const publishMessage = {
      topic: topic,
      payload: payload,
      ...(qos === undefined ? {} : { qos }),
    }

    socket.value.send(JSON.stringify(publishMessage))
  }

  // Demo device error modal management
  const closeDemoDeviceError = (): void => {
    showDemoDeviceError.value = false
    demoDeviceError.value = null
  }

  return {
    messages,
    isConnected,
    status,
    error,
    connectionStatus,
    hasAuthData,
    isConnecting,
    lastError,
    deviceId,
    sessionId,
    demoDeviceError,
    showDemoDeviceError,
    connect,
    disconnect,
    getValue,
    subscribe,
    publish,
    closeDemoDeviceError,
  }
})
